import { FC, PropsWithChildren } from 'react';
import {
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Text,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { toEuro } from '@sales-conversation/src/utils/stringUtils';
import useflowsProductBasketStore from '../../store/zustand/flows-products/flowsProductBasketStore';
import {
  getTotalDiscount,
  getTotalInsurancesPrice,
  getTotalPackagesPrice,
  getGrandTotal,
} from '../../utils/basketCalculations';

const FlowProductsSideBarBasketPriceSummary: FC = () => {
  const { t } = useTranslation();

  // Subscribe to the basket store to trigger re-renders when packages or insurances change
  useflowsProductBasketStore((state) => state.packages);
  useflowsProductBasketStore((state) => state.insurances);

  const insurancesPrice = getTotalInsurancesPrice();
  const packagesPrice = getTotalPackagesPrice();
  const discountAmount = getTotalDiscount();
  const total = getGrandTotal();
  const multipleIntervals = Object.keys(total).length > 1;

  if (Object.keys(total).length === 0) {
    return null;
  }

  return (
    <Card
      mx="4"
      bg="gray.ultraLight"
      borderColor="gray.50"
      boxShadow="none"
      mt="4"
    >
      <Heading as="h3" size="md" mb="4">
        {t('FLOWS_PRODUCT-BASKET_PRICE-SUMMARY_TITLE')}
      </Heading>
      <VStack
        align="stretch"
        spacing="3"
        divider={<Divider borderColor="gray.200" />}
      >
        {Object.keys(total).map((interval) => (
          <PriceSection
            key={interval}
            label={
              multipleIntervals
                ? `${t('Per')} ${t(interval).toLowerCase()}`
                : ''
            }
          >
            <Flex justify="space-between" mb="2" textStyle="largeBody">
              <Text>{t('FLOWS_PRODUCT-BASKET_PRICE-SUMMARY_INSURANCES')}</Text>
              <Text>{toEuro(insurancesPrice[interval])}</Text>
            </Flex>
            <Flex justify="space-between" mb="2" textStyle="largeBody">
              <Text>{t('FLOWS_PRODUCT-BASKET_PRICE-SUMMARY_PACKAGES')}:</Text>
              <Text>{toEuro(packagesPrice[interval])}</Text>
            </Flex>
            <Flex
              justify="space-between"
              mb="2"
              textStyle="largeBody"
              color="primary.500"
            >
              <Text>{t('FLOWS_PRODUCT-BASKET_PRICE-SUMMARY_DISCOUNT')}:</Text>
              <Text>-{toEuro(discountAmount[interval])}</Text>
            </Flex>
          </PriceSection>
        ))}
      </VStack>
      <Divider my="4" borderColor="gray.200" />
      <VStack align="stretch" spacing="2">
        {Object.entries(total).map(([interval, amount]) => (
          <Flex justify="space-between" key={interval}>
            <Text textStyle="h6Headline">
              {`${t('PREMIUM')} ${t('Per').toLowerCase()} ${t(
                interval,
              ).toLowerCase()}`}
              :
            </Text>
            <Text textStyle="h6Headline">{toEuro(amount)}</Text>
          </Flex>
        ))}
        <Text textStyle="smallBody" color="gray.500">
          *{t('FLOWS_PRODUCT-BASKET_PRICE-SUMMARY_FOOTNOTE')}
        </Text>
      </VStack>
    </Card>
  );
};

const PriceSection: FC<PropsWithChildren & { label: string }> = ({
  children,
  label,
}) => {
  return (
    <VStack align="stretch" spacing="2">
      <Text color="gray.500">{label}</Text>
      <Box pl={label ? '4' : '0'}>{children}</Box>
    </VStack>
  );
};

export default FlowProductsSideBarBasketPriceSummary;
